import { useEffect, useState } from 'react';
import { createFilterOptions } from '@mui/material';

import TextOverflowTooltip from '@atom/components/common/tooltip/TextOverflowTooltip';
import { Autocomplete, Chip, List } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { WorkOrderField } from '@atom/types/work';
import {
  CustomFieldView,
  getTaskFieldWidth,
} from '@atom/utilities/taskUtilities';
import { getWorkOrderMultiSelectTitle } from '@atom/utilities/workOrderFieldUtilities';

import classes from './workOrderFields.module.css';

const { ListItemText } = List;

const filterOptions = createFilterOptions<string>({
  matchFrom: 'any',
  limit: 250,
});

interface Props {
  field: WorkOrderField;
  onChange: (fieldId: string, value: any) => void;
  isDisabled?: boolean;
  customFieldView: CustomFieldView;
  width?: string;
  dataCy?: string;
  requiredFieldIds?: Set<string>;
  requiredFieldName?: keyof WorkOrderField;
  // Overrides `requiredFieldName` when more than one requirement applies
  requiredFieldNames?: (keyof WorkOrderField)[];
  isTemplate?: boolean;
  onClick?: () => void;
  error?: boolean;
  helperText?: string;
}

const WorkOrderEnumMultipleField = ({
  field,
  onChange,
  isDisabled,
  customFieldView,
  width,
  dataCy,
  requiredFieldIds = new Set([]),
  requiredFieldName,
  requiredFieldNames,
  isTemplate,
  onClick,
  error,
  helperText,
}: Props) => {
  const [values, setValues] = useState<string[]>([]);
  const [query, setQuery] = useState<string>('');

  const styles = {
    chip: {
      marginTop: '5px',
      marginRight: '5px',
      backgroundColor: colors.neutral.ash,
      borderRadius: '20px',
      opacity: isDisabled ? 0.5 : 1,
    },
  };

  const handleChange = (newValues: string[]) => {
    setValues(newValues);
    onChange(field.id, newValues);
  };

  const handleDeselect = (value: string) => {
    if (isDisabled) {
      return;
    }

    const newValues = values.filter(option => option !== value);
    setValues(newValues);
    onChange(field.id, newValues);
  };

  useEffect(() => {
    setValues(field?.value || []);
    setQuery('');
  }, [field?.value]);

  const fieldWidth = width ?? getTaskFieldWidth(customFieldView);

  const selectStyle = {
    width: fieldWidth,
  };

  const labelColor = requiredFieldIds.has(field.id)
    ? colors.brand.red
    : colors.neutral.dim;

  // template displays requirement on required or requiredOnCreate
  const text = getWorkOrderMultiSelectTitle(
    field,
    requiredFieldNames ??
      (isTemplate ? ['requiredOnCreate', 'required'] : [requiredFieldName]),
  );

  // Filters keep a single-line label; everywhere else long labels wrap
  const isFilter = customFieldView === CustomFieldView.FILTER;

  return (
    <div onClick={onClick}>
      {isFilter ? (
        <TextOverflowTooltip
          width={fieldWidth}
          text={text}
          tooltipText={field.title}
          color={labelColor}
          fontSize={fonts.sm}
        />
      ) : (
        <div
          className={classes.fieldTitle}
          style={{
            color: labelColor,
            maxWidth: fieldWidth,
          }}
        >
          {text}
        </div>
      )}
      <Autocomplete
        id={field?.id}
        style={selectStyle}
        onClick={onClick}
        multiple
        disableClearable
        options={field?.enumeration}
        disabled={isDisabled}
        inputValue={query}
        error={error}
        errorHelperText={helperText}
        filterOptions={filterOptions}
        TextFieldProps={{ onClick }}
        onInputChange={(event, newQuery) => setQuery(newQuery || '')}
        getOptionLabel={item => item}
        placeholder={!values.length ? 'Search' : ''}
        value={values}
        disableCloseOnSelect
        data-cy={dataCy ?? `workInfoField-${field.title}-EnumMultipleInput`}
        onChange={(event, newValues) => {
          handleChange(newValues);
        }}
        renderOption={(optionProps, item) => (
          <li key={item} {...optionProps}>
            <ListItemText primary={item} />
          </li>
        )}
        renderValue={selected => {
          return selected.map(item => (
            <Chip
              key={item}
              style={styles.chip}
              label={item}
              onDelete={() => handleDeselect(item)}
            />
          ));
        }}
      />
    </div>
  );
};

export default WorkOrderEnumMultipleField;
